import React, { useCallback, useEffect } from 'react';
import { useImeiScanner, READER_ELEMENT_ID } from './useImeiScanner';
import { openCameraSettings } from './openCameraSettings';
import * as Styles from './styles';

interface ImeiScannerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImeiScanned: (imei: string) => void;
}

/**
 * Conditionally renders scanner content.
 * Full unmount on close guarantees a clean scanner lifecycle on re-open.
 */
export const ImeiScannerModal: React.FC<ImeiScannerModalProps> = ({
    isOpen,
    onClose,
    onImeiScanned,
}) => {
    if (!isOpen) return null;
    return (
        <ScannerModalContent onClose={onClose} onImeiScanned={onImeiScanned} />
    );
};

interface ScannerModalContentProps {
    onClose: () => void;
    onImeiScanned: (imei: string) => void;
}

const ScannerModalContent: React.FC<ScannerModalContentProps> = ({
    onClose,
    onImeiScanned,
}) => {
    const { status, errorMessage, stopScanning, retry } = useImeiScanner({
        onSuccess: (imei) => {
            onImeiScanned(imei);
            onClose();
        },
    });

    const handleClose = useCallback(() => {
        onClose();
        setTimeout(() => stopScanning(), 0);
    }, [onClose, stopScanning]);

    useEffect(() => {
        const original = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = original;
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') handleClose();
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [handleClose]);

    const isLoading = status === 'initializing' || status === 'ready';
    const showCamera = status === 'ready' || status === 'scanning';

    return (
        <Styles.Overlay>
            <Styles.Backdrop onClick={handleClose} />
            <Styles.Modal
                role="dialog"
                aria-modal="true"
                aria-label="IMEI Barcode Scanner"
            >
                <Styles.Header>
                    <Styles.Title>Scan IMEI Barcode</Styles.Title>
                    <Styles.CloseButton
                        type="button"
                        onClick={handleClose}
                        aria-label="Close scanner"
                    >
                        ✕
                    </Styles.CloseButton>
                </Styles.Header>

                <Styles.Body>
                    {isLoading && (
                        <Styles.LoadingContainer>
                            <Styles.Spinner />
                            <p>Opening camera...</p>
                        </Styles.LoadingContainer>
                    )}

                    <Styles.Reader
                        id={READER_ELEMENT_ID}
                        isVisible={showCamera}
                    />

                    {status === 'error' && (
                        <Styles.StatusContainer>
                            <Styles.ErrorText>
                                {errorMessage ||
                                    'Could not read a valid IMEI. Ensure the barcode is within the frame.'}
                            </Styles.ErrorText>
                            <Styles.Actions>
                                <Styles.PrimaryButton
                                    type="button"
                                    onClick={retry}
                                >
                                    Try Again
                                </Styles.PrimaryButton>
                                <Styles.SecondaryButton
                                    type="button"
                                    onClick={handleClose}
                                >
                                    Enter Manually
                                </Styles.SecondaryButton>
                            </Styles.Actions>
                        </Styles.StatusContainer>
                    )}

                    {status === 'permissionDenied' && (
                        <Styles.StatusContainer>
                            <Styles.ErrorText>
                                Camera access is blocked. Please enable it in
                                your browser settings.
                            </Styles.ErrorText>
                            <Styles.Actions>
                                <Styles.PrimaryButton
                                    type="button"
                                    onClick={openCameraSettings}
                                >
                                    Open Settings
                                </Styles.PrimaryButton>
                                <Styles.SecondaryButton
                                    type="button"
                                    onClick={handleClose}
                                >
                                    Enter Manually
                                </Styles.SecondaryButton>
                            </Styles.Actions>
                        </Styles.StatusContainer>
                    )}

                    {status === 'noCamera' && (
                        <Styles.StatusContainer>
                            <Styles.ErrorText>
                                No camera found on this device.
                            </Styles.ErrorText>
                            <Styles.Actions>
                                <Styles.SecondaryButton
                                    type="button"
                                    onClick={handleClose}
                                >
                                    Enter Manually
                                </Styles.SecondaryButton>
                            </Styles.Actions>
                        </Styles.StatusContainer>
                    )}
                </Styles.Body>
            </Styles.Modal>
        </Styles.Overlay>
    );
};
